import React from "react";
import { Plus, Minus } from "lucide-react";
import { useI18n } from "../i18n/I18nProvider";
import { useCart } from "../context/CartContext";
import { toast } from "sonner";

const ProductCard = ({ product }) => {
    const { t, lang, formatPrice } = useI18n();
    const { add, updateQty, items } = useCart();

    const name = (lang === "sq" ? product.name_sq : product.name_en) || product.name_en || product.name_sq;
    const price = formatPrice(product.price_all);
    const discount = product.original_all > product.price_all
        ? Math.round((1 - product.price_all / product.original_all) * 100)
        : 0;
    const outOfStock = (product.stock ?? 1) <= 0;

    const inCart = items.find((p) => p.id === product.id);
    const qty = inCart?.qty ?? 0;

    const handleAdd = (e) => {
        e?.stopPropagation();
        add(product);
        // Only toast on the very first add — incrementing is silent (less noisy)
        if (qty === 0) {
            toast.success(`${t("toast_added")}: ${name.length > 24 ? name.slice(0, 24) + "…" : name}`);
        }
    };

    const handleDec = (e) => {
        e?.stopPropagation();
        updateQty(product.id, qty - 1);
    };

    return (
        <div
            data-testid={`product-card-${product.id}`}
            className={`group rounded-lg border hover:shadow-md hover:-translate-y-0.5 transition-all duration-300 relative flex flex-col ${
                discount > 0
                    ? "bg-gradient-to-b from-emerald-50 to-white border-emerald-300 shadow-[0_0_0_2px_rgba(16,185,129,0.18)] hover:shadow-[0_4px_18px_rgba(16,185,129,0.35)]"
                    : "bg-white border-neutral-200/80"
            }`}
        >
            {/* Full-name hover tooltip — desktop only (mobile uses native long-press
                via the `title` attribute below). Floats above the card so it never
                pushes neighbouring tiles around. */}
            <div
                className="hidden md:block absolute left-1 right-1 -top-1 -translate-y-full z-30 bg-neutral-900 text-white text-[11px] font-semibold leading-snug px-2 py-1.5 rounded-md shadow-lg opacity-0 group-hover:opacity-100 pointer-events-none transition-opacity duration-200"
                aria-hidden="true"
            >
                {name}
            </div>

            {discount > 0 && (
                <span
                    data-testid={`discount-badge-${product.id}`}
                    className="absolute -top-1.5 -left-1.5 z-10 inline-flex items-center gap-0.5 bg-emerald-500 text-white text-[10px] font-extrabold tracking-wide uppercase px-1.5 py-1 rounded-md shadow-md shadow-emerald-500/40 ring-2 ring-white"
                    style={{ animation: "discountPulse 1.8s ease-in-out infinite" }}
                >
                    SAVE {discount}%
                </span>
            )}
            {outOfStock && (
                <span className="absolute top-1.5 right-1.5 z-10 bg-neutral-900/80 text-white text-[8px] font-bold uppercase px-1 py-0.5 rounded">
                    !
                </span>
            )}
            <div className="relative aspect-square overflow-hidden rounded-t-lg bg-neutral-50">
                <img
                    src={product.image}
                    alt={name}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                    loading="lazy"
                />
            </div>
            <div className="p-1.5 flex-1 flex flex-col">
                <h3
                    title={name}
                    className="text-[11px] font-semibold text-neutral-900 line-clamp-3 min-h-[39px] leading-tight break-words"
                >
                    {name}
                </h3>
                {/* Weight hidden per user spec — kept on the model for shipping math. */}
                <div className="mt-auto pt-1.5 flex items-end justify-between gap-1">
                    <div
                        className="font-display text-[13px] font-semibold leading-none whitespace-nowrap min-w-0 flex items-baseline gap-1"
                        data-testid={`product-price-${product.id}`}
                    >
                        <span className={discount > 0 ? "text-emerald-700 font-extrabold" : "text-neutral-900"}>
                            {price.primary}
                        </span>
                        {discount > 0 && product.original_all > product.price_all && (
                            <span
                                data-testid={`product-original-price-${product.id}`}
                                className="text-[10px] font-semibold text-neutral-400 line-through whitespace-nowrap"
                            >
                                {formatPrice(product.original_all).primary}
                            </span>
                        )}
                    </div>

                    {qty === 0 ? (
                        <button
                            disabled={outOfStock}
                            onClick={handleAdd}
                            data-testid={`add-to-cart-${product.id}`}
                            className="h-7 w-7 rounded-md bg-[#FF5722] hover:bg-[#E64A19] text-white flex items-center justify-center transition-all hover:scale-105 shadow-sm shadow-[#FF5722]/30 disabled:opacity-40 disabled:cursor-not-allowed disabled:hover:scale-100 shrink-0"
                            aria-label={t("product_add")}
                        >
                            <Plus className="h-3.5 w-3.5" strokeWidth={2.5} />
                        </button>
                    ) : (
                        <div
                            data-testid={`qty-stepper-${product.id}`}
                            className="h-7 inline-flex items-center bg-[#FF5722] rounded-md shadow-sm shadow-[#FF5722]/30 shrink-0 select-none"
                        >
                            <button
                                onClick={handleDec}
                                data-testid={`qty-dec-${product.id}`}
                                className="h-7 w-6 text-white hover:bg-[#E64A19] rounded-l-md flex items-center justify-center transition-colors"
                                aria-label={t("product_decrement") || "Remove one"}
                            >
                                <Minus className="h-3 w-3" strokeWidth={2.75} />
                            </button>
                            <span
                                data-testid={`qty-value-${product.id}`}
                                className="text-white text-[11px] font-bold tabular-nums w-5 text-center"
                            >
                                {qty}
                            </span>
                            <button
                                onClick={handleAdd}
                                disabled={outOfStock}
                                data-testid={`qty-inc-${product.id}`}
                                className="h-7 w-6 text-white hover:bg-[#E64A19] rounded-r-md flex items-center justify-center transition-colors disabled:opacity-40"
                                aria-label={t("product_add")}
                            >
                                <Plus className="h-3 w-3" strokeWidth={2.75} />
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default ProductCard;
